import { Reference, StoreObject } from '@apollo/client';
import { InMemoryCacheConfig } from '@apollo/client/cache/inmemory/inMemoryCache';

import introspectionQueryResultData from '../../types/__generated_fragments__.json';
import { merge } from './merge';

type RefOrStoreObject = Reference | StoreObject;

export const cacheConfig: InMemoryCacheConfig = {
  possibleTypes: introspectionQueryResultData.possibleTypes,
  typePolicies: {
    Page: {
      fields: {
        timeline: {
          // Short for options.mergeObjects(existing, incoming). generally behaves the
          // same as { ...existing, ...incoming }, except when the incoming fields have
          // custom merge functions. When options.mergeObjects encounters custom merge
          // functions for any of the fields in its second argument (incoming), those
          // nested merge functions will be called before combining the fields of existing
          // and incoming, as desired
          merge: true,
          // Cache incoming data under the same key regardless
          // of argument values
          keyArgs: false,
        },
        relationships: {
          merge: true,
          keyArgs: false,
        },
        targetWithCurrency: {
          merge: true,
        },
        donationSummary: {
          merge: true,
        },
        donations: {
          merge: true,
          keyArgs: false,
        },
        mediaCollection: {
          merge: true,
          keyArgs: false,
        },
        totals: {
          merge: true,
        },
      },
    },
    PageRelationships: {
      fields: {
        companies: {
          keyArgs: false,
        },
        teams: {
          keyArgs: false,
        },
        events: {
          keyArgs: false,
        },
        campaigns: {
          keyArgs: false,
        },
        beneficiaries: {
          keyArgs: false,
        },
        members: {
          keyArgs: false,
        },
      },
    },
    DonationConnection: {
      fields: {
        edges: {
          merge,
        },
        pageInfo: {
          merge: true,
        },
        totalCount: {
          // Short for always prefer incoming over existing data.
          merge: false,
        },
      },
    },
    PageRelationshipConnection: {
      fields: {
        edges: {
          merge,
        },
        pageInfo: {
          merge: true,
        },
        totalCount: {
          merge: false,
        },
      },
    },
    BeneficiaryConnection: {
      fields: {
        nodes: {
          merge,
        },
        pageInfo: {
          merge: true,
        },
        totalCount: {
          merge: false,
        },
      },
    },
    TimelineEntryConnection: {
      fields: {
        nodes: {
          merge,
          // Read function is setup here so we can order timeline entries by date
          read(existingNodes, { readField, canRead }) {
            if (!existingNodes) {
              return [];
            }

            const nodes = [...existingNodes];

            // Filtering by canRead to remove any dangling references left
            // over after evicting from the cache.
            // https://www.apollographql.com/docs/react/caching/garbage-collection/#dangling-references
            return nodes
              .filter(node => canRead(node))
              .sort((a: RefOrStoreObject, b: RefOrStoreObject) => {
                const createdAtAsDateType = (
                  timelineEntry: RefOrStoreObject,
                ): Date => {
                  const createdAt = readField<string>(
                    'createdAt',
                    timelineEntry,
                  );

                  if (!createdAt) {
                    throw new Error('Can not order new timeline entry');
                  }

                  return new Date(createdAt);
                };

                return (
                  createdAtAsDateType(b).valueOf() -
                  createdAtAsDateType(a).valueOf()
                );
              });
          },
        },
      },
    },
    PageTotals: {
      fields: {
        displayOptions: {
          merge: false,
        },
        primaryMeasurementType: {
          merge: false,
        },
        timeline: {
          merge: false,
        },
      },
    },
    PageTotal: {
      // Page total doesn't have an id field to store the value against in the cache.
      // This will setup this field to be used as the identifier
      keyFields: ['measurementDomain'],
    },
    DonationSummary: {
      fields: {
        totalAmount: {
          merge: true,
        },
      },
    },
    FitnessActivity: {
      merge: true,
    },
  },
};
